import classNames from 'classnames';

import { ProductAccessoriesTranslation } from '../../localization/ModuleTranslation';
import type { PricelistPriceGroupData, PricelistProductAccessoryData } from '../../model';
import Badge from '../../ui/Badge/Badge';
import Price from '../../ui/Price/Price';
import Skeleton from '../../ui/Skeleton/Skeleton';
import { Caption, Cell, HeaderCell, Row, Table, TableBody, TableHead } from '../../ui/Table/Table';
import NetPrice from '../NetPrice/NetPrice';
import css from './ProductAccessories.css';

interface ProductAccessoriesProps {
  accessories: PricelistProductAccessoryData[];
  translation: ProductAccessoriesTranslation;
  caption?: string;
  labelledBy?: string;
  className?: string;
}

const ProductAccessories = ({ accessories, caption, className, labelledBy, translation }: ProductAccessoriesProps) => {
  if (accessories.empty()) {
    return null;
  }

  return (
    <div className={classNames(css.root, className)}>
      <Table aria-labelledby={labelledBy}>
        {caption && <Caption>{caption}</Caption>}
        <TableHead>
          <Row>
            <HeaderCell className={css.cell}>{translation.itemNo}</HeaderCell>
            <HeaderCell className={css.cell}>{translation.name}</HeaderCell>
            <HeaderCell className={classNames(css.cell, css.priceCell)}>{translation.price}</HeaderCell>
            <HeaderCell className={classNames(css.cell, css.priceCell)}>{translation.netPrice.label}</HeaderCell>
          </Row>
        </TableHead>
        <TableBody>
          {accessories.map((accessory) => (
            <Row key={accessory.id}>
              <Cell className={classNames(css.cell, css.itemNoCell)}>
                {accessory.displayOptionsData.showCode ? accessory.code : ''}
              </Cell>
              <Cell className={classNames(css.cell, css.nameCell)}>
                {accessory.name} {accessory.additionalInformation}
                {accessory.annotations.any() &&
                  accessory.annotations.map((annotation, index) => (
                    <div className={css.annotation} key={index}>
                      {annotation}
                    </div>
                  ))}
              </Cell>
              {accessory.displayOptionsData.showPriceOnDemand ? (
                <Cell className={classNames(css.cell, css.priceOnDemandCell)} colSpan={2}>
                  {translation.priceOnDemand}
                </Cell>
              ) : accessory.priceGroups.any() ? (
                <Cell className={css.cell} colSpan={2}>
                  <PriceGroupList accessory={accessory} translation={translation} />
                </Cell>
              ) : (
                <>
                  <Cell className={classNames(css.cell, css.priceCell)}>
                    <Price amount={accessory.listPrice} />
                  </Cell>
                  <Cell className={classNames(css.cell, css.priceCell)}>
                    <NetPrice amount={accessory.netListPrice} translation={translation.netPrice} />
                  </Cell>
                </>
              )}
            </Row>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ProductAccessories;

interface PriceGroupListProps {
  accessory: PricelistProductAccessoryData;
  translation: ProductAccessoriesTranslation;
}

/** The accessory's price groups, followed by its base price when it has one, each with its price and net price. */
const PriceGroupList = ({ accessory, translation }: PriceGroupListProps) => {
  const priceGroups: PricelistPriceGroupData[] = accessory.listPrice
    ? [
        ...accessory.priceGroups,
        { name: translation.basePrice, listPrice: accessory.listPrice, netPrice: accessory.netListPrice },
      ]
    : accessory.priceGroups;

  return (
    <dl className={css.priceGroupList}>
      {priceGroups.map((pg, index) => (
        <div className={css.priceGroupListItem} key={index}>
          <dt className={css.priceGroup}>
            <Badge>{pg.name}</Badge>
          </dt>
          <dd className={css.price}>
            <Price amount={pg.listPrice} />
          </dd>
          <dd className={css.price}>
            <NetPrice amount={pg.netPrice} translation={translation.netPrice} />
          </dd>
        </div>
      ))}
    </dl>
  );
};

interface ProductAccessoriesSkeletonProps {
  rowCount: number;
  translation: ProductAccessoriesTranslation;
  labelledBy?: string;
  className?: string;
}

export const ProductAccessoriesSkeleton = ({
  className,
  labelledBy,
  rowCount,
  translation,
}: ProductAccessoriesSkeletonProps) => {
  return (
    <div className={classNames(css.root, className)}>
      <Table aria-labelledby={labelledBy}>
        <TableHead>
          <Row className={css.headerRowSkeleton}>
            <HeaderCell>{translation.itemNo}</HeaderCell>
            <HeaderCell>{translation.name}</HeaderCell>
            <HeaderCell className={classNames(css.cell, css.priceCell)}>{translation.price}</HeaderCell>
            <HeaderCell className={classNames(css.cell, css.priceCell)}>{translation.netPrice.label}</HeaderCell>
          </Row>
        </TableHead>
        <TableBody>
          {Array.from({ length: rowCount }, (_: unknown, index: number) => (
            <Row className={css.rowSkeleton} key={index}>
              <Cell className={css.itemNoCell}>
                <Skeleton className={css.itemNoSkeleton} />
              </Cell>
              <Cell className={css.nameCell}>
                <Skeleton className={css.nameSkeleton} />
              </Cell>
              <Cell className={classNames(css.cell, css.priceCell)}>
                <Skeleton className={css.priceSkeleton} />
              </Cell>
              <Cell className={classNames(css.cell, css.priceCell)}>
                <Skeleton className={css.priceSkeleton} />
              </Cell>
            </Row>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};
